// 202 -- Live (spectator tab, handoff S13): what's happening right now and
// what's still coming today, from one discover call (published events only,
// optionalAuth -- works anonymously). v1 deliberately shows live/upcoming
// EVENTS, not inline match scores: a cross-event score feed would need a new
// aggregation endpoint; scores are one tap away on each event page.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { DiscoverEvent } from "@dugads/shared";
import { Radio, ChevronRight, CalendarClock } from "lucide-react";
import * as api from "@/lib/api";
import { SchoolCrest } from "@/components/school-crest";

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
}

function EventRow({ event, live }: { event: DiscoverEvent; live: boolean }) {
  return (
    <Link
      to={`/events/${event.id}`}
      data-event-id={event.id}
      className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm transition-transform active:scale-[.99]"
    >
      <SchoolCrest name={event.school_name} logoUrl={event.school_logo_url} primaryColor={event.school_primary_color} size={32} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{event.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {event.school_name}
          {event.sport_name ? ` · ${event.sport_name}` : ""}
          {` · ${fmtTime(event.starts_at)} - ${fmtTime(event.ends_at)}`}
        </p>
      </div>
      {live ? (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-600" aria-hidden /> LIVE
        </span>
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </Link>
  );
}

export default function LivePage() {
  const [events, setEvents] = useState<DiscoverEvent[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);
    api
      .discoverEvents({ start: today, end: today, per_page: 50 })
      .then((res) => {
        if (!cancelled) setEvents(res.events);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setEvents([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (events === null) {
    return <p className="text-muted-foreground" data-page="live" data-state="loading">Loading...</p>;
  }

  const now = Date.now();
  const liveNow = events.filter((e) => new Date(e.starts_at).getTime() <= now && new Date(e.ends_at).getTime() >= now);
  const laterToday = events.filter((e) => new Date(e.starts_at).getTime() > now);

  return (
    <div className="flex flex-col gap-4" data-page="live" data-state={error ? "error" : "ready"}>
      <div data-region="header">
        <h1 className="text-2xl font-semibold">Live</h1>
        <p className="text-sm text-muted-foreground">Events on right now and still to come today.</p>
      </div>

      <section className="flex flex-col gap-2.5" data-region="live-now">
        <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          <Radio className="h-3.5 w-3.5" aria-hidden /> Live now
        </h2>
        {liveNow.length === 0 ? (
          <p className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm" data-region="live-empty">
            Nothing is live right now.
          </p>
        ) : (
          <div className="flex flex-col gap-2.5">
            {liveNow.map((e) => (
              <EventRow key={e.id} event={e} live />
            ))}
          </div>
        )}
      </section>

      {laterToday.length > 0 && (
        <section className="flex flex-col gap-2.5" data-region="later-today">
          <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden /> Later today
          </h2>
          <div className="flex flex-col gap-2.5">
            {laterToday.map((e) => (
              <EventRow key={e.id} event={e} live={false} />
            ))}
          </div>
        </section>
      )}

      <Link to="/discover" className="text-sm font-medium text-primary hover:underline" data-action="browse-all">
        Browse all upcoming events
      </Link>
    </div>
  );
}
